import { useEffect, useRef, useState } from "react";
import { connectSession, type WsIn } from "../lib/ws-client.js";
import { AgentEventView, type TurnEntry } from "./AgentEventView.js";
import {
  listArtifacts,
  artifactDownloadUrl,
  type Artifact,
} from "../lib/api.js";

export function ChatView({ sessionId }: { sessionId: string }) {
  const [entries, setEntries] = useState<TurnEntry[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState<{
    turns: number;
    inputTokens: number;
    outputTokens: number;
  } | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const wsRef = useRef<ReturnType<typeof connectSession> | null>(null);
  const liveTextRef = useRef<string>("");
  const liveIdxRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const replayingRef = useRef<boolean>(false);

  useEffect(() => {
    const ws = connectSession(sessionId, handleFrame);
    wsRef.current = ws;
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [entries]);

  function handleFrame(f: WsIn) {
    if (f.kind === "triage") return;
    if (f.kind === "history_start") {
      replayingRef.current = true;
      setEntries([]);
      liveIdxRef.current = null;
      liveTextRef.current = "";
      return;
    }
    if (f.kind === "history_end") {
      replayingRef.current = false;
      // After replay, refresh downloads panel
      listArtifacts(sessionId).then(setArtifacts).catch(() => {});
      return;
    }
    if (f.kind === "user_echo") {
      setEntries((prev) => [...prev, { kind: "user", text: f.text }]);
      return;
    }
    if (f.kind === "text_delta") {
      if (liveIdxRef.current == null) {
        setEntries((prev) => {
          liveIdxRef.current = prev.length;
          liveTextRef.current = f.text;
          return [...prev, { kind: "agent_text", text: f.text }];
        });
      } else {
        liveTextRef.current += f.text;
        const idx = liveIdxRef.current;
        const text = liveTextRef.current;
        setEntries((prev) =>
          prev.map((e, i) => (i === idx ? { ...e, text } : e)),
        );
      }
      return;
    }
    if (f.kind === "text") {
      liveIdxRef.current = null;
      liveTextRef.current = "";
      setEntries((prev) => [...prev, { kind: "agent_text", text: f.text }]);
      return;
    }
    if (f.kind === "tool_start") {
      liveIdxRef.current = null;
      liveTextRef.current = "";
      setEntries((prev) => [
        ...prev,
        { kind: "tool", toolName: f.name, toolArgs: f.args },
      ]);
      return;
    }
    if (f.kind === "tool_end") {
      setEntries((prev) => {
        for (let i = prev.length - 1; i >= 0; i--) {
          const e = prev[i];
          if (
            e.kind === "tool" &&
            e.toolName === f.name &&
            e.toolDurationMs == null
          ) {
            const copy = [...prev];
            copy[i] = {
              ...e,
              toolDurationMs: f.durationMs,
              toolError: f.isError,
            };
            return copy;
          }
        }
        return prev;
      });
      return;
    }
    if (f.kind === "loop_done") {
      if (!replayingRef.current) setBusy(false);
      setUsage({
        turns: f.turns,
        inputTokens: f.inputTokens,
        outputTokens: f.outputTokens,
      });
      if (!replayingRef.current) {
        // Refresh downloads panel — the agent may have save_binary'd something
        listArtifacts(sessionId).then(setArtifacts).catch(() => {});
      }
      return;
    }
    if (f.kind === "cleared") {
      setEntries([]);
      setUsage(null);
      setBusy(false);
      return;
    }
    if (f.kind === "error") {
      setEntries((prev) => [
        ...prev,
        { kind: "agent_text", text: `Error: ${f.message}` },
      ]);
      setBusy(false);
      return;
    }
  }

  function send() {
    const text = input.trim();
    if (!text || busy) return;

    // Slash commands — match CLI feel
    if (text === "/clear") {
      setInput("");
      wsRef.current?.send({ kind: "clear" });
      return;
    }
    if (text === "/abort") {
      setInput("");
      wsRef.current?.send({ kind: "abort" });
      return;
    }

    // Server echoes user_message back as user_echo which appends the
    // "you > …" row. Avoid double-appending by only sending.
    setInput("");
    setBusy(true);
    liveIdxRef.current = null;
    wsRef.current?.send({ kind: "user_message", text });
  }

  function formatSize(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  }

  return (
    <div className="chat">
      <div className="chat-header">
        <span>
          session {sessionId.slice(0, 8)}{" "}
          <span className="dim">(/clear to reset · /abort to stop)</span>
        </span>
        {usage && (
          <span className="dim">
            {usage.turns} turn{usage.turns !== 1 ? "s" : ""} ·{" "}
            {usage.inputTokens} in / {usage.outputTokens} out
          </span>
        )}
      </div>
      <div className="chat-body">
        <div className="scrollback" ref={scrollRef}>
          {entries.map((e, i) => (
            <AgentEventView key={i} entry={e} />
          ))}
          {busy && entries[entries.length - 1]?.kind !== "tool" && (
            <div className="dim">thinking…</div>
          )}
        </div>
        {artifacts.length > 0 && (
          <aside className="artifacts">
            <div className="artifacts-header">Downloads</div>
            {artifacts.map((a) => (
              <a
                key={a.id}
                className="artifact"
                href={artifactDownloadUrl(sessionId, a.id)}
                download
              >
                <div className="artifact-name">{a.relativePath}</div>
                <div className="dim artifact-meta">
                  {a.source} · {formatSize(a.size)}
                </div>
              </a>
            ))}
          </aside>
        )}
      </div>
      <div className="input-row">
        <input
          value={input}
          placeholder={busy ? "Thinking…  (/abort to stop)" : "Ask the agent…"}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          autoFocus
        />
      </div>
    </div>
  );
}
